import { type CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js/min';

/**
 * Small pure validators shared by the API (business-rule checks that answer 422) and the clients
 * (inline form validation). Zod request schemas in `contracts/` only check the *shape*; these
 * helpers decide whether a well-formed value is acceptable.
 */

// ---------------------------------------------------------------- time zones

/** IANA-looking names only ("Asia/Kolkata", "UTC"): no offsets such as "+05:30". */
const TIME_ZONE_NAME = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/;

/**
 * True for an IANA time zone name the runtime knows (PLAN §4 `TimeZone`). Uses
 * `Intl.DateTimeFormat` rather than `Intl.supportedValuesOf('timeZone')`, which lists only
 * canonical ICU ids (it has "Asia/Calcutta" but not "Asia/Kolkata", and no "UTC").
 */
export function isValidTimeZone(value: string): boolean {
  if (value.length === 0 || value.length > 64 || !TIME_ZONE_NAME.test(value)) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------- display names

/** Longest display name (`workspace_members.display_name`, the `DisplayName` contract). */
export const DISPLAY_NAME_MAX_LENGTH = 80;

/**
 * The display name a membership gets by default: the person's full name (up to 100 characters)
 * cut to 80 code points, like Postgres `char_length`. A display name equal to this value is
 * "still the default" and follows full-name changes.
 */
export function defaultDisplayName(fullName: string): string {
  return Array.from(fullName.trim()).slice(0, DISPLAY_NAME_MAX_LENGTH).join('').trimEnd();
}

// ---------------------------------------------------------------- phone numbers

/** Default region for numbers written without a country code (PLAN §4 `Phone`). */
export const DEFAULT_PHONE_REGION: CountryCode = 'IN';

/**
 * Parses a phone number with `libphonenumber-js` (default region IN) and returns it in E.164
 * (`+919876543210`), or null when it is not a valid number. The API answers `422 invalid_phone`
 * for null.
 */
export function normalizePhone(
  input: string,
  region: CountryCode = DEFAULT_PHONE_REGION,
): string | null {
  const value = input.trim();
  if (value.length === 0 || value.length > 32) return null;
  const parsed = parsePhoneNumberFromString(value, region);
  if (!parsed?.isValid()) return null;
  return parsed.number;
}

// ---------------------------------------------------------------- colours (D-017)

export const HEX_COLOR_PATTERN = /^#[0-9A-Fa-f]{6}$/;

/**
 * Default accent colour: Midnight blue `#2F66E8` (D-600; Akshay chose it on 1 Oct 2026, replacing
 * D-017's deep teal `#0F766E`). White on it is 5.0:1. Workspaces that stored an accent keep it;
 * the Appearance setting resets to this value.
 */
export const DEFAULT_ACCENT_COLOR = '#2F66E8';

/** WCAG AA contrast for normal text; the accent must reach it against white (PLAN §4). */
export const ACCENT_MIN_CONTRAST_RATIO = 4.5;

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance of a `#RRGGBB` colour (0 = black, 1 = white). */
export function relativeLuminance(hex: string): number {
  if (!HEX_COLOR_PATTERN.test(hex)) throw new RangeError(`Not a #RRGGBB colour: ${hex}`);
  const n = Number.parseInt(hex.slice(1), 16);
  const r = channel((n >> 16) & 0xff);
  const g = channel((n >> 8) & 0xff);
  const b = channel(n & 0xff);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two `#RRGGBB` colours, from 1 to 21, rounded to 2 decimals. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return Math.round(((hi + 0.05) / (lo + 0.05)) * 100) / 100;
}

/** True when `hex` is usable as the accent: at least 4.5:1 against white. */
export function isAccentContrastSufficient(hex: string): boolean {
  return contrastRatio(hex, '#FFFFFF') >= ACCENT_MIN_CONTRAST_RATIO;
}

// ---------------------------------------------------------------- email domains

/** A lowercase hostname such as `comhard.co.in` (`auth.allowedEmailDomains`). */
export const EMAIL_DOMAIN_PATTERN =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** The domain part of an email address, lowercased. */
export function emailDomain(email: string): string {
  const at = email.lastIndexOf('@');
  return at === -1 ? '' : email.slice(at + 1).toLowerCase();
}

/** True when the list is empty (any domain) or contains the email's domain. */
export function isEmailDomainAllowed(email: string, allowedDomains: readonly string[]): boolean {
  if (allowedDomains.length === 0) return true;
  return allowedDomains.includes(emailDomain(email));
}

/** "ak•••@comhard.co.in" — the `emailHint` of an OTP challenge. */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  if (at <= 0) return '•••';
  const local = email.slice(0, at);
  const visible = local.length <= 2 ? local.slice(0, 1) : local.slice(0, 2);
  return `${visible}•••${email.slice(at)}`;
}
